import React, { useRef, useState } from 'react';
import ProfileMenuIcon from 'components/atoms/icons/profileMenuIconNew';
import BaseDropdown from 'components/atoms/base-dropdown';
import classNames from 'classnames';
import ProfileIcon from 'components/atoms/icons/profileIconNew';
import useUser from 'utils/useUser';
import LogoutIcon from 'components/atoms/icons/logoutIconNew';
import useLogout from 'utils/hooks/auth/useLogout';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';
import ClinicReviewTimeMenuModal from 'components/molecules/clinic/form/review-time-modal/menu/modal';

import ThemeSwitcher from './theme-switcher';
import s from './styles.module.scss';

interface IProps {
  onClick: () => void;
  setClinicReviewTimeOpened: (opened: boolean) => void;
}

const Body: React.FC<IProps> = ({ onClick, setClinicReviewTimeOpened }) => {
  const { t } = useTranslation();

  const user = useUser();
  const {
    isAdmin,
    isAgencyDoctor,
    isPatient,
    isCriticalOrAtRiskAlertingRole,
    isPractice,
  } = user;
  const { logout } = useLogout();

  return (
    <ul className={s.body}>
      <Link
        className={classNames(s.item, s.name)}
        to={!isCriticalOrAtRiskAlertingRole ? Routes.profile : '/'}
        data-cy="nav-profile__edit"
        onClick={onClick}
      >
        <ProfileIcon />
        {user.fullName}
      </Link>
      <div className={s.links}>
        {!isPatient && (
          <>
            <Link
              data-cy="nav-profile__telemed"
              className={s.item}
              to={Routes.telmed!}
              target="_blank"
              onClick={onClick}
            >
              {t('links.telemed')}
            </Link>
            <Link
              data-cy="nav-profile__bucket-report"
              className={s.item}
              to={Routes.bucketReport!}
              target="_blank"
              onClick={onClick}
            >
              {t('links.filesCom')}
            </Link>
          </>
        )}
        {(isAdmin || isAgencyDoctor) && (
          <Link
            data-cy="nav-profile__aws-connect"
            className={s.item}
            to={Routes.awsConnectURL!}
            target="_blank"
            onClick={onClick}
          >
            {t('links.amazonConnect')}
          </Link>
        )}
        {isPractice && (
          <button
            data-cy="nav-profile__clinic-review-time"
            className={s.item}
            onClick={() => {
              setClinicReviewTimeOpened(true);
            }}
          >
            {t('labels.clinicReviewTime')}
          </button>
        )}
      </div>

      <ThemeSwitcher />

      <li
        className={classNames(s.item, s.logout)}
        onClick={() => {
          logout();
        }}
        data-cy="nav-profile__logout"
      >
        {t('links.logout')}
        <LogoutIcon />
      </li>
    </ul>
  );
};

const HeaderProfileDropdown: React.FC = () => {
  const [opened, setOpened] = useState(false);
  const [clinicReviewTimeOpened, setClinicReviewTimeOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (ref.current) {
      ref.current.click();
    }
  };

  const handleClinicReviewTimeClose = () => {
    setClinicReviewTimeOpened(false);
  };

  return (
    <>
      <BaseDropdown
        offset={[0, 2]}
        placement="bottomRight"
        content={
          <Body
            onClick={handleClose}
            setClinicReviewTimeOpened={setClinicReviewTimeOpened}
          />
        }
        onOpenChange={setOpened}
        zIndex={1020}
        transitionName=""
      >
        <div ref={ref} data-cy="nav-profile">
          <ProfileMenuIcon animate={opened} />
        </div>
      </BaseDropdown>
      {clinicReviewTimeOpened && (
        <ClinicReviewTimeMenuModal
          opened={clinicReviewTimeOpened}
          onClose={handleClinicReviewTimeClose}
        />
      )}
    </>
  );
};

export default HeaderProfileDropdown;
